import { createTheme, ThemeProvider } from '@material-ui/core';
import { useLocalStorageValue } from '@react-hookz/web';
import Header from 'components/Header';
import HeaderWellGuardian from 'components/HeaderWellGuardian';
import Sidebar from 'components/Sidebar';
import { APPS } from 'constants/appModules';
import muiTheme from 'constants/muiTheme';
import { getLastActiveWellbore } from 'helpers/WellHelper';
import { getAppFromPath, getCurrentWellAndWellboreFromPath } from 'helpers/pathParser';
import { useFetchAllCategorizedWells } from 'hooks/useFetchAllCategorizedWells';
import { useFetchWellsWithWellbores } from 'hooks/useFetchWellsWithWellbores';
import { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useSelectWellPlans } from 'redux/Performance/selectors';
import { updateCurrentWellAndWellbore } from 'redux/Wells';
import { useSelectCurrentWell, useSelectCurrentWellbore } from 'redux/Wells/selectors';
import { getCurrentWellInLocalStorage, SIDEBAR_OPEN_STATE_KEY } from 'services/localStorer';
import { WellFullDetailsDTO, WellFullDetailsWithUniqueKeyDTO } from 'types/well';

import styles from './Layout.module.scss';
import { useFetchUser } from './useFetchUser';
import { useTrackPage } from './useTrackPage';

type Props = {
  isMobile?: boolean;
  withMobileWellSelect?: boolean;
};

const Layout: FC<Props> = ({ isMobile, withMobileWellSelect, children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const optionalRouteParams = useParams<{
    wellboreId?: string;
    wellPlanId?: string;
    tuwi?: string;
    tuwbi?: string;
  }>();
  const currentWell: WellFullDetailsDTO | undefined = useSelectCurrentWell();
  const app = getAppFromPath(location.pathname);
  const currentWellbore = useSelectCurrentWellbore();
  const { wells, wellbores } = useFetchWellsWithWellbores();
  const {
    categorizedWells: { favouriteWells, activeSensorWells, activeWellviewWells },
  } = useFetchAllCategorizedWells(true);
  const wellPlans = useSelectWellPlans();
  const { fetchUser } = useFetchUser();
  const [isSidebarOpen, setSidebarOpen] = useLocalStorageValue(SIDEBAR_OPEN_STATE_KEY, true);

  const theme = createTheme(muiTheme);

  useTrackPage();

  useEffect(() => {
    try {
      const { well, wellbore } = getCurrentWellAndWellboreFromPath(
        wells,
        wellbores,
        optionalRouteParams.wellboreId,
        optionalRouteParams.tuwi,
        optionalRouteParams.tuwbi,
        location.search,
        wellPlans,
        optionalRouteParams.wellPlanId,
      );
      if (well || wellbore) {
        dispatch(updateCurrentWellAndWellbore({ well, wellbore }));
      } else if (!currentWell && !currentWellbore) {
        const { wellId, tuwi } = getCurrentWellInLocalStorage();
        let targetWell: WellFullDetailsWithUniqueKeyDTO | undefined;
        if (tuwi) {
          targetWell = wells.find(well => well.tuwi === tuwi);
        }
        if (!targetWell && wellId) {
          targetWell = wells.find(well => well.wellId === wellId);
        }
        if (
          !targetWell &&
          (favouriteWells.length || activeSensorWells.length || activeWellviewWells.length)
        ) {
          targetWell = favouriteWells[0] ?? activeSensorWells[0] ?? activeWellviewWells[0];
        }
        if (!targetWell) return;
        const targetWellbore = getLastActiveWellbore(targetWell);
        dispatch(
          updateCurrentWellAndWellbore({
            well: targetWell,
            wellbore: { ...targetWellbore, well: { ...targetWell, wellbores: undefined } },
          }),
        );
      }
    } catch (error) {
      throw error;
    }
  }, [
    activeSensorWells,
    activeWellviewWells,
    currentWell,
    currentWellbore,
    dispatch,
    favouriteWells,
    location.search,
    optionalRouteParams.tuwbi,
    optionalRouteParams.tuwi,
    optionalRouteParams.wellPlanId,
    optionalRouteParams.wellboreId,
    wellPlans,
    wellbores,
    wells,
  ]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleDrawerToggle = () => setSidebarOpen(previousState => !previousState);

  const getComponentToRender = () => {
    if (app === APPS.WELL_GUARDIAN) {
      return (
        <>
          <HeaderWellGuardian />
          {children}
        </>
      );
    } else if (app === APPS.DRILLX_SMART_OPERATIONS || app === APPS.ENERGY_EFFICIENCY) {
      return (
        <>
          <ThemeProvider theme={theme}>
            <Header
              isMobile={isMobile}
              withMobileWellSelect={withMobileWellSelect}
              setSidebarOpen={setSidebarOpen}
            />
            {children}
          </ThemeProvider>
        </>
      );
    }
  };

  return (
    <div className={styles.container}>
      {!isMobile && <Sidebar isOpen={isSidebarOpen} onSidebarChanged={handleDrawerToggle} />}
      <div className={styles.content}>{getComponentToRender()}</div>
    </div>
  );
};

export default Layout;
